import { loadScript } from '../utils/razorpayUtils';
import { dataService } from './dataService';

export interface RazorpayPlan {
  id: string;
  name: string;
  amount: number; // in paise (₹100 = 10000 paise)
  currency: string;
  interval: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval_count: number;
  description?: string;
}

export interface PaymentResponse {
  success: boolean;
  paymentId?: string;
  subscriptionId?: string;
  error?: string;
}

class RazorpayService {
  private isLoaded = false;
  private razorpay: any = null;

  // Initialize Razorpay
  async initialize(): Promise<void> {
    if (this.isLoaded) return;

    try {
      await loadScript('https://checkout.razorpay.com/v1/checkout.js');
      this.isLoaded = true;
    } catch (error) {
      console.error('Failed to load Razorpay:', error);
      throw error;
    }
  }

  // Create a subscription plan
  async createPlan(plan: RazorpayPlan): Promise<string> {
    try {
      const response = await fetch('/api/razorpay/create-plan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(plan),
      });

      if (!response.ok) {
        throw new Error('Failed to create plan');
      }

      const data = await response.json();
      return data.planId;
    } catch (error) {
      console.error('Error creating plan:', error);
      throw error;
    }
  }

  // Create a subscription
  async createSubscription(planId: string, customerId: string): Promise<string> {
    try {
      const response = await fetch('/api/razorpay/create-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ planId, customerId }),
      });

      if (!response.ok) {
        throw new Error('Failed to create subscription');
      }

      const data = await response.json();
      return data.subscriptionId;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }

  // Process payment
  async processPayment(options: {
    amount: number;
    currency: string;
    subscriptionId?: string;
    customerName: string;
    customerEmail: string;
    customerContact: string;
    description: string;
    tenantId: string;
    planId: string;
  }): Promise<PaymentResponse> {
    try {
      await this.initialize();

      const paymentOptions = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID, // Your Razorpay Key ID
        amount: options.amount,
        currency: options.currency,
        name: 'Cafelawn',
        description: options.description,
        subscription_id: options.subscriptionId,
        customer: {
          name: options.customerName,
          email: options.customerEmail,
          contact: options.customerContact,
        },
        prefill: {
          name: options.customerName,
          email: options.customerEmail,
          contact: options.customerContact,
        },
        notes: {
          tenantId: options.tenantId,
          planId: options.planId,
        },
        theme: {
          color: '#3B82F6',
        },
        handler: (response: any) => {
          this.handlePaymentSuccess(response, options);
        },
        modal: {
          ondismiss: () => {
            this.handlePaymentFailure('Payment cancelled by user');
          },
        },
      };

      const rzp = new (window as any).Razorpay(paymentOptions);
      rzp.open();

      return { success: true };
    } catch (error) {
      console.error('Payment processing error:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Handle successful payment
  private async handlePaymentSuccess(response: any, options: any): Promise<void> {
    try {
      const paymentData = {
        paymentId: response.razorpay_payment_id,
        subscriptionId: response.razorpay_subscription_id,
        tenantId: options.tenantId,
        planId: options.planId,
        amount: options.amount,
        currency: options.currency,
      };

      // Verify payment on server
      const verifyResponse = await fetch('/api/razorpay/verify-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData),
      });

      if (verifyResponse.ok) {
        const result = await verifyResponse.json();
        if (result.success) {
          // Update tenant subscription
          await this.updateTenantSubscription(options.tenantId, options.planId, response.razorpay_subscription_id);
          
          // Show success message
          alert('Payment successful! Your subscription has been activated.');
          window.location.reload(); // Refresh to update UI
        } else {
          alert('Payment verification failed. Please contact support.');
        }
      } else {
        alert('Payment verification failed. Please contact support.');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      alert('Payment verification failed. Please contact support.');
    }
  }

  // Handle payment failure
  private handlePaymentFailure(error: string): void {
    console.error('Payment failed:', error);
    alert('Payment failed. Please try again.');
  }

  // Update tenant subscription in database
  private async updateTenantSubscription(tenantId: string, planId: string, subscriptionId: string): Promise<void> {
    try {
      // Use the data service to update subscription
      await dataService.updateTenantSubscriptionAfterPayment(
        tenantId,
        planId,
        subscriptionId,
        'payment_' + Date.now(), // Generate a payment ID
        'monthly' // Default to monthly, can be enhanced to detect billing cycle
      );
    } catch (error) {
      console.error('Error updating tenant subscription:', error);
      throw error;
    }
  }

  // Get subscription details
  async getSubscriptionDetails(subscriptionId: string): Promise<any> {
    try {
      const response = await fetch(`/api/razorpay/subscription/${subscriptionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to get subscription details');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting subscription details:', error);
      throw error;
    }
  }

  // Cancel subscription
  async cancelSubscription(subscriptionId: string): Promise<boolean> {
    try {
      const response = await fetch(`/api/razorpay/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ subscriptionId }),
      });

      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  }
}

export const razorpayService = new RazorpayService(); 